import React, { useState, useEffect } from 'react';
import { apiClient } from '../../services/apiClient';
import { apiStatsService } from '../../services/apiStatsService';
import { useAuth } from '../../contexts/Auth/authContext';
import { useI18n } from '../../contexts/I18nContext';
import { safeStorage } from '../../utils/safeStorage';
import './StatisticsModal.css';

interface StatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface APIStats {
  totalRequests: number;
  ssoRequests: number;
  apiRequests: number;
  successfulRequests: number;
  failedRequests: number;
  requestHistory: Array<{
    timestamp: string;
    endpoint: string;
    method: string;
    status: number;
    duration: number;
  }>;
}

interface DeviceStats {
  totalDevices: number;
  totalClients: number;
  lastRefresh: string;
}

export const StatisticsModal: React.FC<StatisticsModalProps> = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { formatDateTime, formatTime, t } = useI18n();
  const [deviceStats, setDeviceStats] = useState<DeviceStats>({
    totalDevices: 0,
    totalClients: 0,
    lastRefresh: 'Never'
  });
  const [apiStats, setApiStats] = useState<APIStats>({
    totalRequests: 0,
    ssoRequests: 0,
    apiRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    requestHistory: []
  });
  const [loading, setLoading] = useState(false);

  const refreshStatistics = React.useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      // Fetch device statistics using the same approach as DevicesTable
      const devicesResponse = await apiClient.getAxiosInstance().get('/v2/conf');
      const devices = Array.isArray(devicesResponse.data) ? devicesResponse.data : [];

      // Get unique clients from devices
      const uniqueClients = new Set(
        devices
          .map(device => device.client)
          .filter(client => client && client.trim() !== '')
      );

      setDeviceStats({
        totalDevices: devices.length,
        totalClients: uniqueClients.size,
        lastRefresh: formatDateTime(new Date())
      });

      // Get API statistics from the stats service
      const currentStats = apiStatsService.getStats();
      setApiStats({
        totalRequests: currentStats.totalRequests,
        ssoRequests: currentStats.ssoRequests,
        apiRequests: currentStats.apiRequests,
        successfulRequests: currentStats.successfulRequests,
        failedRequests: currentStats.failedRequests,
        requestHistory: currentStats.requestHistory
      });

    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, formatDateTime]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      refreshStatistics();
    }
  }, [isOpen, isAuthenticated, refreshStatistics]);

  if (!isOpen) return null;

  const formatDuration = (ms: number) => `${ms}ms`;
  const formatTimestamp = (timestamp: string) => formatTime(timestamp);

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return '#28a745';
    if (status >= 300 && status < 400) return '#ffc107';
    if (status >= 400 && status < 500) return '#fd7e14';
    return '#dc3545';
  };

  return (
    <div className="statistics-modal-overlay" onClick={onClose}>
      <div className="statistics-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="statistics-modal-header">
          <h2>📊 Full Statistics</h2>
          <button className="statistics-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="statistics-modal-body">
          {loading ? (
            <div className="statistics-loading">
              <div className="loading-spinner"></div>
              <p>Loading statistics...</p>
            </div>
          ) : (
            <div className="statistics-content">

              {/* Device Statistics */}
              <div className="statistics-section">
                <h3>📱 Device & Client Statistics</h3>
                <div className="statistics-grid">
                  <div className="statistics-item">
                    <label>Total Devices:</label>
                    <span className="statistics-value primary">{deviceStats.totalDevices}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Total Clients:</label>
                    <span className="statistics-value primary">{deviceStats.totalClients}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Last Refresh:</label>
                    <span className="statistics-value">{deviceStats.lastRefresh}</span>
                  </div>
                </div>
              </div>

              {/* API Request Statistics */}
              <div className="statistics-section">
                <h3>🌐 API Request Statistics</h3>
                <div className="statistics-grid">
                  <div className="statistics-item">
                    <label>Total Requests:</label>
                    <span className="statistics-value primary">{apiStats.totalRequests}</span>
                  </div>
                  <div className="statistics-item">
                    <label>SSO API Requests:</label>
                    <span className="statistics-value">{apiStats.ssoRequests}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Device API Requests:</label>
                    <span className="statistics-value">{apiStats.apiRequests}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Successful (2xx):</label>
                    <span className="statistics-value success">{apiStats.successfulRequests}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Failed (4xx/5xx):</label>
                    <span className="statistics-value error">{apiStats.failedRequests}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Success Rate:</label>
                    <span className="statistics-value">
                      {apiStats.totalRequests > 0
                        ? `${Math.round((apiStats.successfulRequests / apiStats.totalRequests) * 100)}%`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Session Information */}
              <div className="statistics-section">
                <h3>🔐 Session Information</h3>
                <div className="statistics-grid">
                  <div className="statistics-item">
                    <label>Authenticated:</label>
                    <span className={`statistics-value ${isAuthenticated ? 'success' : 'error'}`}>
                      {isAuthenticated ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="statistics-item">
                    <label>User ID:</label>
                    <span className="statistics-value">{user?.id || 'N/A'}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Name:</label>
                    <span className="statistics-value">{user?.name || 'N/A'}</span>
                  </div>
                  <div className="statistics-item">
                    <label>Session Start:</label>
                    <span className="statistics-value">
                      {safeStorage.getItem('auth_time')
                        ? formatDateTime(parseInt(safeStorage.getItem('auth_time')!))
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="statistics-item">
                    <label>Token Expires:</label>
                    <span className="statistics-value">
                      {safeStorage.getItem('expires_at')
                        ? formatDateTime(parseInt(safeStorage.getItem('expires_at')!))
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent Request History */}
              <div className="statistics-section">
                <h3>📈 Recent Request History</h3>
                <div className="request-history">
                  {apiStats.requestHistory.length > 0 ? (
                    <div className="request-history-table">
                      <div className="request-history-header">
                        <span>Time</span>
                        <span>Method</span>
                        <span>Endpoint</span>
                        <span>Status</span>
                        <span>Duration</span>
                      </div>
                      {apiStats.requestHistory.slice(-10).reverse().map((request, index) => (
                        <div key={index} className="request-history-row">
                          <span className="request-time">{formatTimestamp(request.timestamp)}</span>
                          <span className="request-method">{request.method}</span>
                          <span className="request-endpoint">{request.endpoint}</span>
                          <span
                            className="request-status"
                            style={{ color: getStatusColor(request.status) }}
                          >
                            {request.status}
                          </span>
                          <span className="request-duration">{formatDuration(request.duration)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-history">
                      <p>No request history available</p>
                      <small>Request tracking will begin after the next API call</small>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}
        </div>

        <div className="statistics-modal-footer">
          <button className="statistics-modal-button secondary" onClick={refreshStatistics}>
            🔄 Refresh
          </button>
          <button className="statistics-modal-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};